import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { getPreview } from '../api';
import { day } from '../format';
import TrendTile from './TrendTile';
import 'flag-icons/css/flag-icons.min.css';
import './Terminal.css';
import './Preview.css';

/* What a visitor sees before signing up: this week's real trends, three of them
 * legible and the rest with their names withheld.
 *
 * Three in full rather than none, because the thing a stranger doubts is not
 * whether the design is nice but whether there is anything behind it. A tile
 * they can read - a term, a company they have heard of, a curve that moved -
 * settles that in a way no amount of marketing copy does. The withheld ones
 * still carry their curve and their change, so the page reads as a week with
 * things happening in it rather than a wall of grey.
 *
 * It renders through the same TrendTile the signed-in grid uses, so this cannot
 * quietly become a flattering version of the product. */

export default function Preview() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPreview()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  // The split is the server's, not ours. Trimming a full list in the browser
  // would leave every withheld term sitting in the network tab.
  const shown = data?.open || [];
  const locked = data?.locked || [];
  const total = data?.total || 0;
  const week = data?.week || null;
  const hidden = Math.max(0, total - shown.length);

  if (isLoaded && isSignedIn) return <Navigate to="/trends" replace />;

  return (
    <div className="tw pv">
      <div className="tw-page">
        <div className="tw-page-head">
          <div>
            <h1 className="tw-page-title">This week&rsquo;s open trends</h1>
            <p className="tw-page-sub">
              Search terms that have broken out of their own normal range and are
              still there. Each one is read by a language model that explains what
              is driving it and scores how material it is to the company.
              {week ? ` Week ending ${day(week)}.` : ''}
            </p>
          </div>
          {!loading && total > 0 && (
            <span className="tw-page-count">{total}</span>
          )}
        </div>

        {loading && (
          <div className="tw-grid">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div className="tw-tile is-loading" key={i}>
                <div className="tw-shimmer" style={{ width: '55%' }} />
                <div className="tw-shimmer" style={{ width: '35%', height: 8 }} />
                <div className="tw-shimmer" style={{ width: '100%', height: 28 }} />
              </div>
            ))}
          </div>
        )}

        {!loading && total === 0 && (
          <div className="tw-empty">
            <div>
              <strong>Nothing is open this week.</strong> No term has broken out of
              its normal range and held there.
            </div>
            <div>
              That is the usual state of the world, and it is why the list is
              short. Sign up and we will show you the week it is not.
            </div>
            <button type="button" className="cta" onClick={() => navigate('/signup')}>
              Create a free account
            </button>
          </div>
        )}

        {!loading && total > 0 && (
          <>
            <div className="tw-grid">
              {shown.map((s) => (
                <TrendTile key={s.episode_id} signal={s} points={s.points} />
              ))}
            </div>

            {hidden > 0 && (
              <div className="pv-locked">
                <div className="tw-grid" aria-hidden="true">
                  {locked.map((s) => (
                    <TrendTile key={s.episode_id} signal={s}
                      points={s.points} locked />
                  ))}
                </div>

                <div className="pv-gate">
                  <div className="pv-gate-card">
                    <h2>{hidden} more {hidden === 1 ? 'trend' : 'trends'} open right now</h2>
                    <p>
                      Free account, no card. You get the full list, the write-up
                      behind each move, and every company and term we scan.
                    </p>
                    <div className="pv-gate-actions">
                      <button type="button" className="cta"
                        onClick={() => navigate('/signup')}>
                        Create a free account
                      </button>
                      <button type="button" className="cta ghost"
                        onClick={() => navigate('/signin')}>
                        Sign in
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
